import {
  addDays,
  addMonths,
  addYears,
  isAfter,
  isBefore,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import RegEx from "../_constantes/regexConstantes.json";
import { formatApiDate } from "./formatters";
import { isEqual } from "lodash";

const patternTelephone = new RegExp(RegEx.telephone);
const patternTelephonePortable = new RegExp(RegEx.telephonePortable);
const patternTelephoneFixe = new RegExp(RegEx.telephoneFixe);
const patternAlphaNumeric = new RegExp(RegEx.alphaNumeric);
const patternAlphaNumericHyphenUnderscore = new RegExp("^[0-9a-zA-ZÀ-ú-_]+$");
const patternApe = new RegExp(RegEx.codeApe);
// a-zA-ZÀ-ú-_
const patternAlphaHyphenUnderscore = new RegExp("^[a-zA-ZÀ-ú-_]+$");
const patternAlphaAndSpace = new RegExp("^[a-zA-ZÀ-ú ]+$");

export const dateFormat = "dd/MM/yyyy";
export const dateFormatApi = "yyyy-MM-DD";
export const dateFormatYearMonth = "MM/yyyy";

/** age en jours */
export const maxEnfantsAge = addYears(new Date(), -25);
export const minEnfantsAge = addYears(new Date(), 0);
export const maxLocataireAge = addDays(new Date(), -36499);
export const minLocataireAge = addDays(new Date(), -5840);

export const isTelephone = (value: string): boolean => {
  const num = value.replace(/\s/g, "");
  return patternTelephone.test(num);
};

export const isTelephonePortable = (value: string): boolean => {
  const num = value.replace(/\s/g, "");
  return patternTelephonePortable.test(num);
};

export const isTelephoneFixe = (value: string): boolean => {
  const num = value.replace(/\s/g, "");
  return patternTelephoneFixe.test(num);
};

export const isEmail = (value: string): boolean =>
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    value
  );

export const isMotDePasse = (value: string): boolean =>
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{8,}$/.test(value);

export const isAlphaNumeric = (value: string): boolean =>
  patternAlphaNumeric.test(value);

export const isAlphaNumericHyphenAndUnderscore = (value: string): boolean =>
  patternAlphaNumericHyphenUnderscore.test(value);

export const isAlphaHyphenAndUnderscore = (value: string): boolean =>
  patternAlphaHyphenUnderscore.test(value);
export const isAlphaAndSpace = (value: string): boolean =>
  patternAlphaAndSpace.test(value);
export const isAgeEnfantCorrect = (value: string): boolean => {
  const dateNaissance = new Date(value);
  return dateNaissance >= maxEnfantsAge && dateNaissance <= minEnfantsAge;
};

export const isAgeLocataireCorrect = (value: string): boolean => {
  const dateNaissance = new Date(value);
  return dateNaissance >= maxLocataireAge && dateNaissance <= minLocataireAge;
};

export const isActiviteAvantXjours = (
  dateDebutSituation: string | undefined,
  nombreJours: number
) => {
  const dateLimitDebutActivite = addDays(new Date(), nombreJours);
  const dateActivite =
    (dateDebutSituation && new Date(dateDebutSituation)) || new Date();

  return dateLimitDebutActivite > dateActivite;
};

export const isActiviteAvant1Month = (
  dateDebutSituation: string | undefined
) => {
  const dateLimitDebutActivite = addMonths(new Date(), -1);
  const dateActivite =
    (dateDebutSituation && new Date(dateDebutSituation)) || new Date();

  return isBefore(dateActivite, dateLimitDebutActivite);
};
export const isActiviteAvantDernierBulletin = (
  dateDebutSituation: string | undefined,
  dateDernierBulletin: string | undefined,
  numeroMois: number
) => {
  if (dateDebutSituation && dateDernierBulletin) {
    const getMonth = dateDernierBulletin && startOfMonth(dateDernierBulletin);
    const dateBulletin = subMonths(getMonth, numeroMois);

    const dateActivite =
      dateDebutSituation && startOfDay(new Date(dateDebutSituation));

    return (
      isBefore(dateActivite, dateBulletin) ||
      isEqual(dateActivite, dateBulletin)
    );
  } else return false;
};

export const isDateDebutSituationPlus1an = (
  dateDebutSituation: string | undefined
) => {
  const dateDebutSituationStart =
    dateDebutSituation && startOfDay(dateDebutSituation);
  const todayStart = startOfDay(new Date());
  return (
    dateDebutSituationStart &&
    isBefore(addYears(dateDebutSituationStart, 1), todayStart)
  );
};

export const isDateDebutSituationPlusOrEqual1an = (
  dateDebutSituation: string | undefined
) => {
  const dateDebutSituationStart =
    dateDebutSituation && startOfDay(dateDebutSituation);
  const todayStart = startOfDay(new Date());
  return (
    dateDebutSituationStart &&
    (isBefore(addYears(dateDebutSituationStart, 1), todayStart) ||
      isEqual(addYears(dateDebutSituationStart, 1), todayStart))
  );
};

export const isDateInfAujourdhui = (date: string) =>
  isBefore(startOfDay(date), startOfDay(new Date())) ||
  isEqual(startOfDay(date), startOfDay(new Date()));

export const getDateApiFromString = (date?: string) =>
  date && formatApiDate(date);

export const compareDates = (date1: string, date2: string, strict: boolean) =>
  strict
    ? isAfter(startOfDay(date1), startOfDay(date2))
    : isEqual(startOfDay(date1), startOfDay(date2)) ||
      isAfter(startOfDay(date1), startOfDay(date2));

export const estSiretValide = (siret: string): boolean => {
  const siretFormatte = (siret && siret.replace(/\s/g, "")) || siret;
  let estValide =
    (siretFormatte &&
      siretFormatte.length === 14 &&
      !isNaN(Number(siretFormatte))) ||
    false;

  if (estValide) {
    // Donc le SIRET est un numérique à 14 chiffres
    // Les 9 premiers chiffres sont ceux du SIREN (ou RCS), les 4 suivants
    // correspondent au numéro d'établissement
    // et enfin le dernier chiffre est une clef de LUHN.
    let somme = 0;
    let tmp;
    for (let cpt = 0; cpt < siretFormatte.length; cpt++) {
      if (cpt % 2 === 0) {
        // Les positions impaires : 1er, 3è, 5è, etc...
        tmp = Number(siretFormatte.charAt(cpt)) * 2; // On le multiplie par 2
        // Si le résultat est supérieur à 9, on lui soustrait 9
        tmp = tmp > 9 ? (tmp -= 9) : tmp;
      } else {
        tmp = siretFormatte.charAt(cpt);
      }

      somme += Number(tmp);
    }

    // Si la somme est un multiple de 10 alors le SIRET est valide
    estValide = somme % 10 === 0;
  }
  return estValide;
};

export const estApeValide = (codeApe: string): boolean =>
  patternApe.test(codeApe);

export const isValidationPossible = (date: Date): boolean => {
  const todayMoins20 = subDays(new Date(), 20);
  return new Date(date) >= todayMoins20;
};

export const isInteger = (value: number): boolean => Number.isInteger(+value);
